import { useState } from 'react'

const CATEGORY_STYLES = {
  chore:       'bg-blue-100 text-blue-700',
  improvement: 'bg-emerald-100 text-emerald-700',
}
const CATEGORY_ICON = { chore: '🧹', improvement: '✨' }

const TAG_COLORS = [
  'bg-violet-100 text-violet-700', 'bg-pink-100 text-pink-700',
  'bg-orange-100 text-orange-700', 'bg-teal-100 text-teal-700',
  'bg-cyan-100 text-cyan-700',     'bg-rose-100 text-rose-700',
  'bg-lime-100 text-lime-700',     'bg-amber-100 text-amber-700',
]

function tagColor(tag) {
  if (!tag) return 'bg-gray-100 text-gray-500'
  let h = 0
  for (const c of tag) h = (h * 31 + c.charCodeAt(0)) & 0xffff
  return TAG_COLORS[h % TAG_COLORS.length]
}

function formatDate(dateStr) {
  if (!dateStr) return null
  const [y, m, d] = dateStr.split('-').map(Number)
  return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    .format(new Date(y, m - 1, d))
}

// "Good" is applied automatically; only show the two explicit overrides
const OVERRIDE_RATINGS = [
  { value: 'satisfactory', label: '😐 Was less',  mult: 0.5  },
  { value: 'awesome',      label: '⭐ Amazing!',  mult: 1.25 },
]
const RATING_STARS = { satisfactory: '⭐', good: '⭐⭐', awesome: '⭐⭐⭐' }

export default function TaskCard({ task, onToggleDone, onDelete, onEvaluate, onEdit, namespace }) {
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [evaluating,    setEvaluating]    = useState(false)
  const [editing,       setEditing]       = useState(false)
  const [editTitle,     setEditTitle]     = useState(task.title)
  const [editAmount,    setEditAmount]    = useState(task.base_amount ?? '')

  const kids       = namespace?.slug === 'nishtha'
  const isObsolete = task.obsolete

  function handleDeleteClick() {
    if (!confirmDelete) {
      setConfirmDelete(true)
      setTimeout(() => setConfirmDelete(false), 3000)
    } else {
      onDelete(task.id)
    }
  }

  function handleEditKeyDown(e) {
    if (e.key === 'Enter')  saveEdit()
    if (e.key === 'Escape') { setEditing(false); setEditTitle(task.title); setEditAmount(task.base_amount ?? '') }
  }

  async function saveEdit() {
    const trimmed = editTitle.trim()
    setEditing(false)
    if (!trimmed) { setEditTitle(task.title); return }
    const fields = { title: trimmed }
    const amt = parseFloat(editAmount)
    if (!isNaN(amt) && amt >= 0) fields.base_amount = amt
    else if (editAmount === '') fields.base_amount = null
    await onEdit(task.id, fields)
  }

  async function handleEvaluate(rating) {
    setEvaluating(true)
    try { await onEvaluate(task.id, rating) }
    finally { setEvaluating(false) }
  }

  // ── Obsolete state ───────────────────────────────────────
  if (isObsolete) {
    return (
      <div className="group bg-gray-50 rounded-xl border border-dashed border-gray-200 p-4 opacity-60">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 flex-shrink-0 text-lg">{kids ? '😅' : '💨'}</span>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-gray-400 line-through leading-snug">{task.title}</p>
            <span className="inline-block mt-1 text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
              {kids ? 'Oops, missed this one' : 'Not completed in time'}
            </span>
          </div>
          <button
            onClick={handleDeleteClick}
            className="opacity-40 group-hover:opacity-100 p-1.5 rounded-lg transition
              text-gray-400 hover:text-red-500 hover:bg-red-50 focus:outline-none focus:opacity-100"
            aria-label="Delete task"
          >
            {confirmDelete ? (
              <span className="text-xs font-medium text-red-600">Sure?</span>
            ) : (
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 16 16">
                <path d="M3 4h10M5 4V3a1 1 0 011-1h4a1 1 0 011 1v1M6 7v5M10 7v5M4 4l1 9h6l1-9"
                  stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
          </button>
        </div>
      </div>
    )
  }

  // ── Normal card ──────────────────────────────────────────
  const cardClass = task.done
    ? kids ? 'border-purple-100 bg-purple-50/30 opacity-75' : 'border-gray-100 opacity-60'
    : kids
      ? 'border-purple-200 hover:border-purple-300 hover:shadow-sm'
      : 'border-gray-200 hover:border-indigo-200 hover:shadow-sm'

  const checkDoneClass = task.done
    ? kids ? 'bg-purple-500 border-purple-500 text-white' : 'bg-indigo-600 border-indigo-600 text-white'
    : kids ? 'border-purple-300 hover:border-purple-500' : 'border-gray-300 hover:border-indigo-400'

  // Show override panel only when auto-"good" was applied; hide once explicitly rated
  const showEvaluate = kids && task.done && task.evaluation === 'good'

  return (
    <div className={`group bg-white rounded-xl border transition-all duration-200 p-4 ${cardClass}`}>
      <div className="flex items-start gap-3">
        {/* Checkbox / star */}
        <button
          onClick={() => onToggleDone(task)}
          aria-label={task.done ? 'Mark as not done' : 'Mark as done'}
          className={`mt-0.5 flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center
            transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:cursor-default
            ${kids ? 'focus:ring-purple-400' : 'focus:ring-indigo-400'} ${checkDoneClass}`}
        >
          {task.done && (
            kids
              ? <span className="text-sm leading-none">⭐</span>
              : <svg className="w-3 h-3" viewBox="0 0 12 12" fill="none">
                  <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
          )}
        </button>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-2 flex-wrap">
            {editing ? (
              <div
                className="flex-1 min-w-0 flex items-center gap-2 flex-wrap"
                onBlur={e => {
                  // Only save when focus leaves this entire group
                  if (!e.currentTarget.contains(e.relatedTarget)) saveEdit()
                }}
              >
                <input
                  autoFocus
                  value={editTitle}
                  onChange={e => setEditTitle(e.target.value)}
                  onKeyDown={handleEditKeyDown}
                  className="flex-1 min-w-0 text-sm font-medium leading-snug text-gray-800
                    border-b border-indigo-400 outline-none bg-transparent pb-0.5"
                />
                {kids && (
                  <label className="flex items-center gap-1 text-xs text-gray-500 flex-shrink-0">
                    <span className="text-gray-400">₹</span>
                    <input
                      type="number"
                      min="0"
                      step="0.5"
                      value={editAmount}
                      onChange={e => setEditAmount(e.target.value)}
                      onKeyDown={handleEditKeyDown}
                      placeholder="reward"
                      className="w-20 text-xs border-b border-purple-400 outline-none bg-transparent pb-0.5 text-purple-700"
                    />
                  </label>
                )}
              </div>
            ) : (
              <p
                className={`text-sm font-medium leading-snug flex-1 min-w-0 ${
                  task.done ? 'line-through text-gray-400' : 'text-gray-800'
                }`}
              >
                {task.title}
              </p>
            )}
            {task.recurrence_days == null && task.template_id && (
              <span className={`flex-shrink-0 inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${
                kids ? 'bg-purple-100 text-purple-600' : 'bg-indigo-100 text-indigo-600'
              }`}>
                🔁 repeating
              </span>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2 mt-1.5">
            {task.tag && (
              <span className={`inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full ${tagColor(task.tag)}`}>
                {task.tag}
              </span>
            )}
            {task.category && (
              <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${CATEGORY_STYLES[task.category]}`}>
                {CATEGORY_ICON[task.category]} {task.category}
              </span>
            )}
            {task.minutes != null && (
              <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 16 16">
                  <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.5"/>
                  <path d="M8 5v3.5l2.5 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                {task.minutes} min
              </span>
            )}
            {task.available_from && !task.done && (
              <span className="inline-flex items-center gap-1 text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 16 16">
                  <path d="M8 1.5a6.5 6.5 0 100 13 6.5 6.5 0 000-13z" stroke="currentColor" strokeWidth="1.5"/>
                  <path d="M8 5v3h3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                from {formatDate(task.available_from)}
              </span>
            )}
            {/* Reward badge — shown before evaluation */}
            {kids && task.base_amount && !task.earned && !task.done && (
              <span className="inline-flex items-center gap-1 text-xs font-medium bg-violet-100 text-violet-700 px-2 py-0.5 rounded-full">
                💰 ₹{parseFloat(task.base_amount).toFixed(0)} reward
              </span>
            )}

            {/* Earned badge */}
            {task.earned != null && (
              <span className="inline-flex items-center gap-1 text-xs font-medium bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                💰 ₹{parseFloat(task.earned).toFixed(2)} {RATING_STARS[task.evaluation]}
              </span>
            )}
          </div>

          {/* Evaluation override panel — shown when auto "good" is active */}
          {showEvaluate && (
            <div className={`mt-3 pt-3 border-t border-purple-100 ${evaluating ? 'opacity-50 pointer-events-none' : ''}`}>
              <p className="text-xs font-semibold text-purple-700 mb-2">
                Earned ₹{task.base_amount ? parseFloat(task.base_amount).toFixed(0) : '0'} ⭐⭐ — override?
              </p>
              <div className="flex gap-2">
                {OVERRIDE_RATINGS.map(({ value, label, mult }) => (
                  <button
                    key={value}
                    onClick={() => handleEvaluate(value)}
                    className="flex-1 flex flex-col items-center py-1.5 px-1 rounded-xl
                      bg-purple-50 hover:bg-purple-100 text-purple-700 transition
                      focus:outline-none focus:ring-2 focus:ring-purple-400"
                  >
                    <span className="text-sm font-medium">{label}</span>
                    {task.base_amount && (
                      <span className="text-xs text-purple-500 mt-0.5">
                        ₹{(parseFloat(task.base_amount) * mult).toFixed(0)}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Edit + Delete */}
        <div className="flex-shrink-0 flex items-center gap-1 opacity-40 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
          {/* Edit */}
          {!editing && !task.done && (
            <button
              onClick={() => { setEditing(true); setEditTitle(task.title) }}
              aria-label="Edit task"
              className="p-1.5 rounded-lg text-gray-400 hover:text-indigo-500 hover:bg-indigo-50
                focus:outline-none focus:text-indigo-500"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 16 16">
                <path d="M11 2l3 3-8 8H3v-3l8-8z" stroke="currentColor" strokeWidth="1.5"
                  strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          )}

          {/* Delete */}
          <button
            onClick={handleDeleteClick}
            aria-label={confirmDelete ? 'Confirm delete' : 'Delete task'}
            className={`p-1.5 rounded-lg focus:outline-none transition-colors
              ${confirmDelete
                ? 'bg-red-600 text-white'
                : 'text-gray-400 hover:text-red-500 hover:bg-red-50'
              }`}
          >
            {confirmDelete ? (
              <span className="text-xs font-medium px-0.5">Sure?</span>
            ) : (
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 16 16">
                <path d="M3 4h10M5 4V3a1 1 0 011-1h4a1 1 0 011 1v1M6 7v5M10 7v5M4 4l1 9h6l1-9"
                  stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
